/* A compact text snapshot of a project for the AI copilot to reason over. */
import type { Project } from "./types";

function countBy(arr: any[], key: string): string {
  const m: Record<string, number> = {};
  for (const x of arr) { const k = x[key] || "?"; m[k] = (m[k] || 0) + 1; }
  return Object.entries(m).map(([k, n]) => `${k} ${n}`).join(", ");
}

export function projectSnapshot(p: Project): string {
  const L: string[] = [];
  L.push(`Project: ${p.name} (${p.code}) — ${p.type}${p.location ? `, ${p.location}` : ""}. Client: ${p.client?.name ?? "?"}${p.client?.company ? ` / ${p.client.company}` : ""}.`);
  L.push(`Budget ${p.budget} ${p.currency}. Appointed ${p.createdOn}.`);
  const stages = (p as any).stages ?? [];
  if (stages.length) L.push(`Stages: ${stages.map((s: any) => `${s.label}=${s.status}`).join(", ")}.`);
  if (p.brief) L.push(`Brief status: ${p.brief.status}.`);
  if (p.engagement) L.push(`Engagement: ${p.engagement.status}.`);
  const dels = p.deliverables ?? [];
  const drawn = dels.filter((d) => d.previewUrl || d.fileUrl).length;
  if (dels.length) L.push(`Drawings: ${dels.length} sheets (${drawn} drawn) — ${countBy(dels, "discipline")}. Buildings: ${(p.buildings ?? []).join(", ") || "one"}.`);
  if (p.billing) { const b = p.billing; L.push(`Billing: fee/contract ${b.contractSum} ${b.currency}; ${(b.milestones ?? []).length} milestones; retainer ${b.retainer ? `${b.retainer.pct}% = ${b.retainer.amount}` : "none"}; ${b.issuedOn ? "issued to client" : "not issued"}.`); }
  const inv = p.invoices ?? [];
  if (inv.length) L.push(`Invoices (${inv.length}): ${inv.slice(0, 24).map((i) => `${i.ref} "${i.title}" ${i.amount} ${i.status}${i.dueOn ? ` due ${i.dueOn}` : ""}`).join("; ")}.`);
  const rv = p.reviews ?? [];
  if (rv.length) L.push(`Reviews: ${rv.map((r) => { let open = 0, resp = 0, appr = 0; for (const s of r.sheets) for (const m of s.remarks) if (m.author === "client") { if (!m.response) open++; else { resp++; if (m.response.accepted) appr++; } } return `"${r.title}" [${r.status}, ${open} open, ${resp} addressed, ${appr} approved]`; }).join("; ")}.`);
  const rfi = p.rfis ?? [];
  if (rfi.length) L.push(`RFIs: ${rfi.map((r) => `${r.ref} "${r.subject}" [${r.status}, ball:${r.ballInCourt}]`).join("; ")}.`);
  const vs = p.variations ?? [];
  if (vs.length) L.push(`Variations: ${vs.map((v) => `${v.ref} "${v.title}" ${v.amount} [${v.status}]`).join("; ")}.`);
  const site = p.siteLog ?? [];
  if (site.length) { const pct = site.find((u) => typeof u.percent === "number")?.percent; L.push(`Site: ${site.length} updates, latest "${site[0]?.title}"${pct != null ? `, ${pct}% complete` : ""}.`); }
  const con = p.consultants ?? [];
  if (con.length) L.push(`Consultants: ${con.map((c) => `${c.discipline} ${c.firm} [${c.status}]`).join("; ")}.`);
  return L.join("\n");
}

/** The context the concept drafter needs: the brief in the client's own words,
 *  the accommodation, the site, the money, and the mood the client has set. */
export function conceptContext(p: Project): string {
  const L: string[] = [];
  L.push(`Project: ${p.name} — ${p.type}${p.location ? `, ${p.location}` : ""}.`);
  L.push(`Client: ${p.client?.name ?? "the client"}${p.client?.company ? ` (${p.client.company})` : ""}.`);
  if (p.gia) L.push(`Overall area: ${p.gia} ${p.giaUnit === "sqm" ? "m²" : "ft²"}.`);
  if ((p as any).floors > 1) L.push(`Storeys: ${(p as any).floors}.`);
  const cb = p.costBrief;
  if (cb) L.push(`Preliminary construction budget: ${cb.budget} ${cb.currency} at ${cb.quality} level of finish (${cb.ratePerUnit} per ${cb.unit === "sqm" ? "m²" : "ft²"}).`);
  else if (p.budget) L.push(`Construction budget: ${p.budget} ${p.currency}.`);
  if (p.surveyPlan) L.push(`Survey plan: ${p.surveyPlan}.`);
  if ((p as any).geo) L.push(`Site coordinates on record (do not infer site features from these).`);

  for (const s of (p.brief?.sections ?? [])) if (s.body?.trim()) L.push(`Brief — ${s.title}: ${s.body.slice(0, 900)}`);
  const spaces = (p.brief as any)?.spaces ?? [];
  if (spaces.length) {
    const unit = (p.brief as any)?.areaUnit === "sqm" ? "m²" : "ft²";
    L.push(`Accommodation schedule: ${spaces.map((s: any) => `${s.name} ×${s.qty} @ ${s.area}${unit}${s.zone ? ` [${s.zone}]` : ""}`).join("; ")}`);
  }
  const sw = p.moodboard?.swatches ?? [];
  if (sw.length) L.push(`Mood board palette the client responded to: ${sw.map((s: any) => `${s.name}${s.material ? ` (${s.material})` : ""}`).join(", ")}.`);
  if ((p.moodboard?.images ?? []).length) L.push(`The client has shared ${p.moodboard!.images.length} reference images on the mood board.`);
  const cons = (p.concepts ?? []);
  if (cons.length) L.push(`Existing directions on record: ${cons.map((c: any) => `${c.title}${c.response?.choice === "preferred" ? " (client prefers this)" : ""}`).join("; ")}.`);
  return L.join("\n");
}
